import React, { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Search, Upload, CheckCircle2, ShieldCheck, Clock, AlertCircle, FileText, X, Download, Users, Eye } from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { psAPI, allocationRequestsAPI, holdingsAPI, formatDate, formatCurrency } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const statusBadgeConfig = {
    staged: { label: 'Staged', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    'l1_approved': { label: 'Pending L2', color: 'bg-blue-100 text-blue-800', icon: ShieldCheck },
    executing: { label: 'Executing', color: 'bg-purple-100 text-purple-800', icon: Clock },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
    failed: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: AlertCircle },
    pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: Clock },
    ready: { label: 'Ready', color: 'bg-indigo-100 text-indigo-800', icon: Clock },
    processing: { label: 'Processing', color: 'bg-purple-100 text-purple-800', icon: Clock }
};

const renderStatusBadge = (distribution) => {
    if (!distribution) {
        return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                <Clock className="w-3.5 h-3.5 mr-1" />
                No Stage
            </span>
        );
    }
    const { status } = distribution;
    const config = statusBadgeConfig[status] || statusBadgeConfig.staged;
    const Icon = config.icon;
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            <Icon className="w-3.5 h-3.5 mr-1" />
            {config.label}
        </span>
    );
};

const formatToken = (value) => new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
}).format(Number(value || 0));

function BondTokenAllocation() {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const currentUserId = user?._id || user?.id;
    const isSubAccount = user?.isSubAccount || !!user?.parentAccountId;
    const parentAccountId = user?.parentAccountId;
    const [searchTerm, setSearchTerm] = useState('');
    const [uploadContext, setUploadContext] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [detailsContext, setDetailsContext] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [csvErrors, setCsvErrors] = useState([]);
    const [csvWarnings, setCsvWarnings] = useState([]);
    const [csvStats, setCsvStats] = useState(null);
    const [parsedData, setParsedData] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [manualAllocations, setManualAllocations] = useState({});
    const [editingInvestor, setEditingInvestor] = useState(null); // Format: 'bondId-investorId'
    const [finalizeApprovalStates, setFinalizeApprovalStates] = useState({});
    const [approvedAllocations, setApprovedAllocations] = useState({}); // Track approved allocations by bondId-investorId
    const [bondTokenRequests, setBondTokenRequests] = useState({}); // Track bond token requests by bondId-investorId
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1); // Reset to first page on search
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const downloadTemplate = () => {
        const csvContent = [
            'Investor ID,LEI,Bond Amount',
            '507f1f77bcf86cd799439011,XYZABC1234567890WXYZ,10000000',
            '507f1f77bcf86cd799439012,ABCXYZ9876543210LMNO,15000000',
            '507f1f77bcf86cd799439013,LMNOPQ1122334455RRTT,25000000',
            '# Note: Use actual Investor User IDs (24-char ObjectId) who subscribed through this PS',
            '# Investor must be either a Custodian or a Direct Investor for this bond',
            '# LEI must be exactly 20 characters (Legal Entity Identifier)',
            '# LEI must match the investor\'s registered LEI in the database',
            '# Total token amount cannot exceed your PS held tokens',
            '# Any remaining tokens will remain with the PS'
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'bond_token_distribution_template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const parseCSV = (csvText, psHeldTokens = 0) => {
        const lines = csvText.trim().split('\n');
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));

        // Validate headers - flexible matching
        const normalizedHeaders = headers.map(h => h.toLowerCase().trim());
        const flexibleHeaderMapping = {
            'investor id': 'investorId',
            'investorid': 'investorId',
            'id': 'investorId',
            'lei': 'lei',
            'legal entity identifier': 'lei',
            'legalentityidentifier': 'lei',
            'token amount': 'tokenAmount',
            'tokenamount': 'tokenAmount',
            'amount': 'tokenAmount',
            'tokens': 'tokenAmount'
        };

        // Create header index mapping
        const headerIndexMap = {};
        normalizedHeaders.forEach((header, index) => {
            const mappedField = flexibleHeaderMapping[header];
            if (mappedField) {
                headerIndexMap[mappedField] = index;
            }
        });

        // Check required fields
        const requiredFields = ['investorId', 'lei', 'tokenAmount'];
        const mappedFields = Object.keys(headerIndexMap);
        const missingFields = requiredFields.filter(field => !mappedFields.includes(field));

        if (missingFields.length > 0) {
            return {
                data: [],
                errors: [`Missing required columns: ${missingFields.join(', ')}. Please use the template.`],
                stats: null
            };
        }

        const data = [];
        const errors = [];
        let totalTokenAmount = 0;

        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();

            // Skip empty lines and comment lines
            if (!line || line.startsWith('#')) continue;

            const values = line.split(',').map(v => v.trim().replace(/"/g, ''));

            const row = {
                investorId: values[headerIndexMap.investorId] || '',
                lei: values[headerIndexMap.lei] || '',
                tokenAmount: values[headerIndexMap.tokenAmount] || ''
            };

            // Validate Investor ID (MongoDB ObjectId - 24 hex characters)
            if (!row.investorId || !/^[0-9a-fA-F]{24}$/.test(row.investorId)) {
                errors.push(`Row ${i}: Invalid Investor ID "${row.investorId}". Must be 24-character ObjectId.`);
                continue;
            }

            // Validate LEI (exactly 20 characters)
            if (!row.lei || row.lei.length !== 20) {
                errors.push(`Row ${i}: Invalid LEI "${row.lei}". Must be exactly 20 characters.`);
                continue;
            }

            // Validate Token Amount
            const tokenAmount = parseFloat(row.tokenAmount);
            if (isNaN(tokenAmount) || tokenAmount <= 0) {
                errors.push(`Row ${i}: Invalid bond amount "${row.tokenAmount}". Must be a positive number.`);
                continue;
            }

            // Check for duplicate investor IDs
            if (data.some(d => d.investorId === row.investorId)) {
                errors.push(`Row ${i}: Duplicate Investor ID "${row.investorId}".`);
                continue;
            }

            totalTokenAmount += tokenAmount;

            data.push({
                investorId: row.investorId,
                lei: row.lei,
                tokenAmount: tokenAmount
            });
        }

        // Validate total doesn't exceed PS held tokens
        if (totalTokenAmount > psHeldTokens) {
            errors.push(
                `Total bond amount (${formatToken(totalTokenAmount)}) exceeds PS held tokens (${formatToken(psHeldTokens)}). ` +
                `Please reduce allocations by ${formatToken(totalTokenAmount - psHeldTokens)}.`
            );
        }

        if (data.length === 0 && errors.length === 0) {
            errors.push('No valid data rows found in CSV file.');
        }

        return {
            data,
            errors,
            stats: {
                totalRows: lines.length - 1,
                validRows: data.length,
                totalTokenAmount
            }
        };
    };

    const validateWithBackend = async (parsedAllocations, bondId) => {
        try {
            const response = await psAPI.validateHoldingDistribution(bondId, {
                allocations: parsedAllocations
            });
            return response?.data?.data || { valid: true, errors: [], warnings: [] };
        } catch (error) {
            const message = error?.response?.data?.message || error?.message || 'Validation failed';
            return {
                valid: false,
                errors: [message],
                warnings: []
            };
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.name.toLowerCase().endsWith('.csv')) {
            toast.error('Please upload a CSV file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File size exceeds 5MB limit');
            return;
        }

        setUploadFile(file);
        setIsUploading(true);
        setUploadProgress(0);
        setCsvErrors([]);
        setCsvWarnings([]);
        setCsvStats(null);
        setParsedData([]);
        setShowPreview(false);

        try {
            // Simulate progress
            setUploadProgress(10);

            const fileContent = await file.text();
            setUploadProgress(30);

            // Parse CSV
            const parseResult = parseCSV(fileContent, uploadContext.psHeldTokens);
            setUploadProgress(50);

            if (parseResult.errors.length > 0) {
                setCsvErrors(parseResult.errors);
                setCsvStats(parseResult.stats);
                setParsedData(parseResult.data);
                setIsUploading(false);
                return;
            }

            // Validate with backend
            setUploadProgress(70);
            const validationResult = await validateWithBackend(parseResult.data, uploadContext.bondId);
            setUploadProgress(90);

            const errors = [...(validationResult.errors || [])];
            const warnings = [...(validationResult.warnings || [])];

            // Validate that all investors are custodians or direct investors
            if (allSubInvestorData?.investors) {
                const bondSubInvestors = allSubInvestorData.investors.filter(inv => {
                    return inv.bonds?.some(b => {
                        const bId = typeof b.bondId === 'string' ? b.bondId : b.bondId?._id || b.bondId;
                        return String(bId) === String(uploadContext.bondId);
                    });
                });

                // Create a map of valid investor IDs (custodians and direct investors)
                const validInvestorIds = new Set();
                const validInvestorDetails = new Map();

                bondSubInvestors.forEach(investor => {
                    if (investor.custodianId) {
                        // This is a sub-investor under a custodian - add custodian ID
                        const custodianId = typeof investor.custodianId === 'string'
                            ? investor.custodianId
                            : investor.custodianId?._id || investor.custodianId;
                        const custodianIdStr = String(custodianId);
                        validInvestorIds.add(custodianIdStr);

                        if (!validInvestorDetails.has(custodianIdStr)) {
                            validInvestorDetails.set(custodianIdStr, {
                                type: 'custodian',
                                name: investor.custodianName || 'Unknown Custodian',
                                subInvestors: []
                            });
                        }
                        validInvestorDetails.get(custodianIdStr).subInvestors.push(investor.name || investor.email);
                    } else {
                        // This is a direct investor - add their ID
                        const userId = String(investor.userId);
                        validInvestorIds.add(userId);

                        validInvestorDetails.set(userId, {
                            type: 'direct',
                            name: investor.name || investor.email || 'Unknown Investor'
                        });
                    }
                });

                // Check each CSV investor
                parseResult.data.forEach((row, index) => {
                    const investorIdStr = String(row.investorId);

                    if (!validInvestorIds.has(investorIdStr)) {
                        errors.push(
                            `Row ${index + 2}: Investor ID ${row.investorId} did not subscribe through this Primary Subscriber. ` +
                            `Only custodians (whose sub-investors subscribed through this PS) and direct investors who have submitted LOC through this PS can receive allocations.`
                        );
                    }
                });
            }

            if (errors.length > 0) {
                setCsvErrors(errors);
            }

            if (warnings.length > 0) {
                setCsvWarnings(warnings);
            }

            setCsvStats({
                ...parseResult.stats,
                serverValidated: validationResult.valid && errors.length === 0
            });
            setParsedData(parseResult.data);
            setShowPreview(true);
            setUploadProgress(100);

        } catch (error) {
            const message = error?.message || 'Failed to process CSV file';
            toast.error(message);
            setCsvErrors([message]);
        } finally {
            setIsUploading(false);
        }
    };

    const {
        data: bondSummariesResponse,
        isLoading,
        isFetching,
        refetch: refetchBondSummaries
    } = useQuery({
        queryKey: ['ps-holding-distributions', 'bonds', currentPage, itemsPerPage, debouncedSearchTerm],
        queryFn: async () => {
            const response = await psAPI.getHoldingDistributionBonds({
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearchTerm
            });
            return response?.data || { data: [], pagination: { total: 0, page: 1, limit: itemsPerPage, totalPages: 0 } };
        },
        staleTime: 0, // Always consider data stale to force refetch
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        keepPreviousData: true // Keep previous data while fetching new data
    });

    const bondSummaries = bondSummariesResponse?.data || [];
    const pagination = bondSummariesResponse?.pagination || { total: 0, page: 1, limit: itemsPerPage, totalPages: 0 };

    const { data: activeDistribution, isLoading: detailLoading } = useQuery({
        queryKey: ['ps-holding-distribution', detailsContext?.bondId],
        queryFn: async () => {
            if (!detailsContext) {
                return null;
            }
            const response = await psAPI.getHoldingDistribution(detailsContext.bondId);
            return response?.data?.data || null;
        },
        enabled: !!detailsContext
    });

    // Fetch all investor LOC requests for all bonds (investors who subscribed through this PS)
    const { data: allSubInvestorData, isLoading: allSubInvestorsLoading, refetch: refetchSubInvestors } = useQuery({
        queryKey: ['ps-sub-investor-loc-all'],
        queryFn: async () => {
            const response = await allocationRequestsAPI.getPsSubInvestorRequests();
            const data = response.data?.data || response.data || { investors: [], summary: {} };
            return data;
        },
        staleTime: 0, // Always consider data stale to force refetch
        refetchOnMount: true,
        refetchOnWindowFocus: true
    });

    // Fetch holdings to track which investors already have approved allocations
    const { data: holdingsData, refetch: refetchHoldings } = useQuery({
        queryKey: ['ps-holdings-as-holder'],
        queryFn: async () => {
            const response = await holdingsAPI.getHoldingsAsHolder({ status: 'active' });
            const holdings = response?.data?.data || [];
            return holdings;
        },
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true
    });

    const uploadMutation = useMutation({
        mutationFn: async ({ bondId, allocations }) => {
            // Create bond token requests for each investor - they will need to approve individually
            const promises = allocations.map(allocation =>
                psAPI.createBondTokenRequest(bondId, {
                    investorId: allocation.investorId,
                    tokenAmount: allocation.tokenAmount
                })
            );
            const results = await Promise.all(promises);
            return results;
        },
        onSuccess: async (data, variables) => {
            const count = variables.allocations.length;
            toast.success(`${count} bond token request${count > 1 ? 's' : ''} created successfully from CSV. Investors will need to approve them.`);

            // Invalidate and refetch all related queries
            await Promise.all([
                queryClient.invalidateQueries(['ps-holding-distributions']),
                queryClient.invalidateQueries(['ps-sub-investor-loc-all']),
                queryClient.invalidateQueries(['ps-holding-distribution', variables.bondId]),
                queryClient.invalidateQueries(['ps-holdings-as-holder'])
            ]);

            // Force refetch to ensure data is updated
            await Promise.all([
                queryClient.refetchQueries(['ps-holding-distributions']),
                queryClient.refetchQueries(['ps-sub-investor-loc-all']),
                queryClient.refetchQueries(['ps-holdings-as-holder'])
            ]);

            // Refetch bond token requests for this bond
            try {
                const response = await psAPI.getBondTokenRequests(variables.bondId);
                const requests = response?.data?.data || [];
                const requestsMap = {};
                requests.forEach(request => {
                    const investorId = typeof request.investorId === 'string'
                        ? request.investorId
                        : request.investorId?._id || request.investorId;
                    const key = `${variables.bondId}-${investorId}`;
                    requestsMap[key] = request;
                });
                setBondTokenRequests(prev => ({ ...prev, ...requestsMap }));
            } catch (error) {
                console.error('Failed to refetch bond token requests:', error);
            }

            // Close modal after successful data refetch
            setUploadContext(null);
            setUploadFile(null);
            setCsvErrors([]);
            setCsvWarnings([]);
            setCsvStats(null);
            setParsedData([]);
            setShowPreview(false);
        },
        onError: (error) => {
            const message = error?.response?.data?.message || error?.message || 'Failed to create bond token requests from CSV';
            toast.error(message);
        }
    });

    const approvalMutation = useMutation({
        mutationFn: async ({ bondId, level }) => {
            const response = await psAPI.approveHoldingDistribution(bondId, level);
            return response?.data;
        },
        onSuccess: (_, variables) => {
            toast.success(`${variables.level} approval completed`);
            queryClient.invalidateQueries(['ps-holding-distributions']);
            queryClient.invalidateQueries(['ps-holding-distribution', variables.bondId]);
        },
        onError: (error) => {
            const message = error?.response?.data?.message || error?.message || 'Approval failed';
            toast.error(message);
        }
    });

    const allocationMutation = useMutation({
        mutationFn: async ({ bondId, investorId, amount }) => {
            // Create bond token request - investor will need to approve it
            const response = await psAPI.createBondTokenRequest(bondId, {
                investorId,
                tokenAmount: amount
            });
            return response?.data;
        },
        onSuccess: async (data, variables) => {
            toast.success('Bond token request created successfully. Investor will need to approve it.');
            // Invalidate and refetch all related queries
            await Promise.all([
                queryClient.invalidateQueries(['ps-holding-distributions']),
                queryClient.invalidateQueries(['ps-sub-investor-loc-all']),
                queryClient.invalidateQueries(['ps-holding-distribution', variables.bondId]),
                queryClient.invalidateQueries(['ps-holdings-as-holder'])
            ]);
            // Force refetch to ensure data is updated
            await queryClient.refetchQueries(['ps-holding-distributions']);
            await queryClient.refetchQueries(['ps-sub-investor-loc-all']);
            await queryClient.refetchQueries(['ps-holdings-as-holder']);
            // Refetch bond token requests for this bond
            try {
                const response = await psAPI.getBondTokenRequests(variables.bondId);
                const requests = response?.data?.data || [];
                const requestsMap = {};
                requests.forEach(request => {
                    const investorId = typeof request.investorId === 'string'
                        ? request.investorId
                        : request.investorId?._id || request.investorId;
                    const key = `${variables.bondId}-${investorId}`;
                    requestsMap[key] = request;
                });
                setBondTokenRequests(prev => ({ ...prev, ...requestsMap }));
            } catch (error) {
                console.error('Failed to refetch bond token requests:', error);
            }
            setEditingInvestor(null);
            setManualAllocations({});
        },
        onError: (error) => {
            const message = error?.response?.data?.message || error?.message || 'Bond token request creation failed';
            toast.error(message);
        }
    });

    const bulkAllocationMutation = useMutation({
        mutationFn: async ({ bondId, allocations }) => {
            // Create bond token requests for each investor - they will need to approve individually
            const promises = allocations.map(allocation =>
                psAPI.createBondTokenRequest(bondId, {
                    investorId: allocation.investorId,
                    tokenAmount: allocation.tokenAmount
                })
            );
            const results = await Promise.all(promises);
            return results;
        },
        onSuccess: async (data, variables) => {
            const count = variables.allocations.length;
            toast.success(`${count} bond token request${count > 1 ? 's' : ''} created successfully. Investors will need to approve them.`);
            // Invalidate and refetch all related queries
            await Promise.all([
                queryClient.invalidateQueries(['ps-holding-distributions']),
                queryClient.invalidateQueries(['ps-sub-investor-loc-all']),
                queryClient.invalidateQueries(['ps-holding-distribution', variables.bondId]),
                queryClient.invalidateQueries(['ps-holdings-as-holder'])
            ]);
            // Force refetch to ensure data is updated
            await queryClient.refetchQueries(['ps-holding-distributions']);
            await queryClient.refetchQueries(['ps-sub-investor-loc-all']);
            await queryClient.refetchQueries(['ps-holdings-as-holder']);
            // Refetch bond token requests for this bond
            try {
                const response = await psAPI.getBondTokenRequests(variables.bondId);
                const requests = response?.data?.data || [];
                const requestsMap = {};
                requests.forEach(request => {
                    const investorId = typeof request.investorId === 'string'
                        ? request.investorId
                        : request.investorId?._id || request.investorId;
                    const key = `${variables.bondId}-${investorId}`;
                    requestsMap[key] = request;
                });
                setBondTokenRequests(prev => ({ ...prev, ...requestsMap }));
            } catch (error) {
                console.error('Failed to refetch bond token requests:', error);
            }
            setEditingInvestor(null);
            setManualAllocations({});
        },
        onError: (error) => {
            const message = error?.response?.data?.message || error?.message || 'Bulk bond token request creation failed';
            toast.error(message);
        }
    });

    const approveFinalize = async (summary, level) => {
        if (!summary) return;
        try {
            await approvalMutation.mutateAsync({ bondId: summary.bondId, level });
            // Reload approval state
            const response = await psAPI.getHoldingDistribution(summary.bondId);
            const distribution = response?.data?.data;
            if (distribution) {
                setFinalizeApprovalStates(prev => ({
                    ...prev,
                    [summary.bondId]: {
                        l1FinalizeApprovedBy: distribution.l1ApprovedBy?._id || distribution.l1ApprovedBy,
                        l2FinalizeApprovedBy: distribution.l2ApprovedBy?._id || distribution.l2ApprovedBy
                    }
                }));
            }
        } catch (e) {
            console.error('Approval failed:', e);
        }
    };

    // Track approved allocations based on holdings data
    useEffect(() => {
        if (!holdingsData || !Array.isArray(holdingsData)) return;

        const approved = {};
        const currentPsId = String(currentUserId);

        holdingsData.forEach(holding => {
            // Track holdings where:
            // 1. The PS is the holder (holderId = PS)
            // 2. But the investorId is different (investorId = the investor who received the allocation)
            // This identifies tokens that have been allocated from PS to investors
            const holdingBondId = typeof holding.bondId === 'string' ? holding.bondId : holding.bondId?._id || holding.bondId;
            const holdingInvestorId = typeof holding.investorId === 'string' ? holding.investorId : holding.investorId?._id || holding.investorId;
            const holdingHolderId = typeof holding.holderId === 'string' ? holding.holderId : holding.holderId?._id || holding.holderId;

            // Only count holdings where:
            // - The PS is the holder (tokens held by PS for someone else)
            // - The investor is NOT the PS (tokens allocated to another investor)
            // - There are remaining tokens
            if (holdingBondId && holdingInvestorId && holdingHolderId &&
                holdingHolderId === currentPsId &&
                holdingInvestorId !== currentPsId &&
                holding.remainingTokenAmount > 0) {
                const key = `${holdingBondId}-${holdingInvestorId}`;
                // If there's already an entry, add to the token amounts
                if (approved[key]) {
                    approved[key].tokenAmount += holding.tokenAmount;
                    approved[key].remainingTokenAmount += holding.remainingTokenAmount;
                } else {
                    approved[key] = {
                        tokenAmount: holding.tokenAmount,
                        remainingTokenAmount: holding.remainingTokenAmount,
                        holdingId: holding._id,
                        blockchainTxHash: holding.blockchainTxHash,
                        createdAt: holding.createdAt
                    };
                }
            }
        });

        setApprovedAllocations(approved);
    }, [holdingsData, currentUserId]);

    // Fetch finalize approval state for each bond after bonds load
    useEffect(() => {
        const loadFinalizeStates = async () => {
            const states = {};
            for (const summary of bondSummaries) {
                try {
                    const response = await psAPI.getHoldingDistribution(summary.bondId);
                    const distribution = response?.data?.data;
                    if (distribution) {
                        states[summary.bondId] = {
                            l1FinalizeApprovedBy: distribution.l1ApprovedBy?._id || distribution.l1ApprovedBy,
                            l2FinalizeApprovedBy: distribution.l2ApprovedBy?._id || distribution.l2ApprovedBy,
                            status: distribution.status
                        };
                    }
                } catch (error) {
                    console.error('Failed to load approval state for bond:', summary.bondId, error);
                }
            }
            setFinalizeApprovalStates(states);
        };
        if (bondSummaries.length > 0) {
            loadFinalizeStates();
        }
    }, [bondSummaries]);

    // Fetch bond token requests for each bond after bonds load
    useEffect(() => {
        const loadBondTokenRequests = async () => {
            const requestsMap = {};
            for (const summary of bondSummaries) {
                try {
                    const response = await psAPI.getBondTokenRequests(summary.bondId);
                    const requests = response?.data?.data || [];

                    // Map requests by investorId for quick lookup
                    requests.forEach(request => {
                        const investorId = typeof request.investorId === 'string'
                            ? request.investorId
                            : request.investorId?._id || request.investorId;
                        const key = `${summary.bondId}-${investorId}`;
                        requestsMap[key] = request;
                    });
                } catch (error) {
                    console.error('Failed to load bond token requests for bond:', summary.bondId, error);
                }
            }
            setBondTokenRequests(requestsMap);
        };
        if (bondSummaries.length > 0) {
            loadBondTokenRequests();
        }
    }, [bondSummaries]);

    const filteredBonds = useMemo(() => {
        // Filtering is now done on backend
        return bondSummaries;
    }, [bondSummaries]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleItemsPerPageChange = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1); // Reset to first page when changing items per page
    };

    if (isLoading || allSubInvestorsLoading) {
        return <LoadingSpinner size="lg" text="Loading bond token allocation..." />;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="px-4 py-8 mx-auto space-y-6 max-w-7xl sm:px-6 lg:px-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Bond Token Allocation</h1>
                        <p className="text-sm text-gray-500">
                            Review PS-held bond tokens and distribute them to investors (custodians and direct investors)
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {isFetching && <span className="text-xs text-gray-500">Refreshing...</span>}
                        <button
                            onClick={downloadTemplate}
                            className="flex items-center space-x-2 btn-primary"
                        >
                            <Download className="w-4 h-4" />
                            <span>Download Template</span>
                        </button>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div className="relative">
                            <Search className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
                            <input
                                type="text"
                                className="form-input pl-10 w-full"
                                placeholder="Search bonds by name, ISIN, or number..."
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    title="Clear search"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        {debouncedSearchTerm && (
                            <div className="mt-2 text-xs text-gray-600">
                                {isFetching ? (
                                    <span className="flex items-center gap-1">
                                        <Clock className="w-3 h-3 animate-spin" />
                                        Searching...
                                    </span>
                                ) : (
                                    <span>
                                        Found {pagination.total} bond{pagination.total !== 1 ? 's' : ''} matching "{debouncedSearchTerm}"
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="space-y-6">
                    {filteredBonds.length === 0 && !isLoading && (
                        <div className="card">
                            <div className="py-10 text-center card-body">
                                <div className="flex flex-col items-center text-gray-500">
                                    <FileText className="w-12 h-12 mb-4 text-gray-300" />
                                    <p className="text-lg font-medium">No bonds found</p>
                                    <p className="text-sm">
                                        {debouncedSearchTerm
                                            ? 'No bonds match your search criteria. Try adjusting your search.'
                                            : 'Confirm there are holdings under this PS.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {filteredBonds.map((summary) => {
                        const { bond, psHeldTokens, psRemainingTokens, psAllocatedTokens, latestDistribution, bondId } = summary;

                        // Calculate actual distributed amount from approved allocations and completed requests
                        const actualDistributed = Object.entries(approvedAllocations)
                            .filter(([key]) => key.startsWith(`${bondId}-`))
                            .reduce((sum, [, info]) => sum + (info.remainingTokenAmount || 0), 0);

                        // Also add completed bond token requests that might not be in holdings yet
                        const completedRequestsTotal = Object.entries(bondTokenRequests)
                            .filter(([key, request]) =>
                                key.startsWith(`${bondId}-`) &&
                                request.status === 'completed'
                            )
                            .reduce((sum, [key, request]) => {
                                // Only count if not already in approved allocations
                                const isInApproved = approvedAllocations[key];
                                return isInApproved ? sum : sum + (request.bondTokenAmount || 0);
                            }, 0);

                        const totalDistributed = actualDistributed + completedRequestsTotal;

                        // Calculate remaining balance: Original allocation - Already distributed
                        const availableForAllocation = psHeldTokens - totalDistributed;

                        // Filter investors for this specific bond
                        const bondSubInvestors = (allSubInvestorData?.investors || []).filter(inv => {
                            return inv.bonds?.some(b => {
                                const bId = typeof b.bondId === 'string' ? b.bondId : b.bondId?._id;
                                return bId === bondId;
                            });
                        }).map(investor => {
                            const bondData = investor.bonds?.find(b => {
                                const bId = typeof b.bondId === 'string' ? b.bondId : b.bondId?._id;
                                return bId === bondId;
                            });
                            return {
                                ...investor,
                                bondData
                            };
                        });

                        // Separate direct investors and group sub-investors by custodian
                        const directInvestors = [];
                        const custodianGroups = new Map();

                        bondSubInvestors.forEach(investor => {
                            if (investor.custodianId) {
                                // Sub-investor - group by custodian
                                const custodianId = investor.custodianId;
                                if (!custodianGroups.has(custodianId)) {
                                    custodianGroups.set(custodianId, {
                                        custodianId: custodianId,
                                        custodianName: investor.custodianName || 'Unknown Custodian',
                                        custodianEmail: investor.custodianEmail || '',
                                        userId: custodianId,
                                        lei: '', // Custodians don't have LEI in this context
                                        name: investor.custodianName || 'Unknown Custodian',
                                        email: investor.custodianEmail || '',
                                        isCustodian: true,
                                        subInvestors: [],
                                        bondData: {
                                            totalAmount: 0
                                        }
                                    });
                                }

                                const group = custodianGroups.get(custodianId);
                                group.subInvestors.push(investor);
                                group.bondData.totalAmount += investor.bondData?.totalAmount || 0;

                                // Use custodian's LEI if available
                                if (!group.lei) {
                                    group.lei = investor.lei || '';
                                }
                            } else {
                                // Direct investor - add to direct investors list
                                directInvestors.push({
                                    ...investor,
                                    isCustodian: false,
                                    isDirect: true
                                });
                            }
                        });

                        // Combine direct investors and custodians for display
                        const displayInvestors = [
                            ...directInvestors,
                            ...Array.from(custodianGroups.values())
                        ];

                        return (
                            <div key={bondId} className="bg-white border border-gray-200 rounded-lg shadow-sm">
                                {/* Bond Header */}
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="flex items-center space-x-2">
                                                <h3 className="text-lg font-medium text-gray-900">{bond?.bondName || 'Unnamed Bond'}</h3>
                                                {bond?.tokenDetails?.symbol && (
                                                    <span className="px-2 py-0.5 text-xs font-semibold bg-primary-100 text-primary-800 rounded">
                                                        {bond.tokenDetails.symbol}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center mt-1 space-x-4 text-sm text-gray-500">
                                                <span>ISIN: {bond?.isin || '—'}</span>
                                                <span>•</span>
                                                <span>Bond #: {bond?.bondNumber || '—'}</span>
                                                <span>•</span>
                                                <span>{bond?.productType === "Fixed Notes" ? "Conventional" : (bond?.productType || '—')}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <div className="flex items-center gap-2">
                                                {renderStatusBadge(latestDistribution)}
                                                {(latestDistribution?.status === 'staged' || latestDistribution?.status === 'l1_approved') && (() => {
                                                    const state = finalizeApprovalStates[bondId];

                                                    // Check if L1 is approved and L2 is not yet approved
                                                    if (state?.l1FinalizeApprovedBy && !state?.l2FinalizeApprovedBy) {
                                                        // Check if the current user (not parent) was L1 approver
                                                        const l1ApproverId = String(state.l1FinalizeApprovedBy);
                                                        const effectiveUserId = String(currentUserId);

                                                        // Disabled only if the SAME user (current sub-account) was L1 approver
                                                        // Sub-accounts and parent accounts are treated as separate users
                                                        const disabled = l1ApproverId === effectiveUserId;

                                                        const hasApprovePermission = true;
                                                        const isDisabled = disabled || !hasApprovePermission;
                                                        return (
                                                            <button
                                                                disabled={isDisabled}
                                                                onClick={() => {
                                                                    if (isDisabled) return;
                                                                    approveFinalize(summary, 'L2');
                                                                }}
                                                                className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${isDisabled
                                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                                    : 'text-white bg-indigo-600 hover:bg-indigo-700'
                                                                    } transition-colors`}
                                                                title={disabled ? 'L1 and L2 approver cannot be the same user or account' : !hasApprovePermission ? 'No permission to approve' : 'Approve as L2'}
                                                            >
                                                                <CheckCircle2 className="w-3 h-3 mr-1" /> L2 Approve
                                                            </button>
                                                        );
                                                    }

                                                    // Show L2 Approved badge if L2 approval is done
                                                    if (state?.l2FinalizeApprovedBy) {
                                                        return (
                                                            <div className="inline-flex items-center px-2 py-1 text-xs text-indigo-700 bg-indigo-100 rounded-md">
                                                                <CheckCircle2 className="w-3 h-3 mr-1" /> L2 Approved
                                                            </div>
                                                        );
                                                    }

                                                    return null;
                                                })()}
                                            </div>
                                            <button
                                                className={`btn-secondary btn-sm flex items-center space-x-1`}
                                                onClick={() => {
                                                    setUploadContext({ ...summary, psHeldTokens: availableForAllocation });
                                                    setUploadFile(null);
                                                    setCsvErrors([]);
                                                    setCsvWarnings([]);
                                                    setCsvStats(null);
                                                    setParsedData([]);
                                                    setShowPreview(false);
                                                }}
                                            >
                                                <Upload className="w-3.5 h-3.5" />
                                                <span>Upload CSV</span>
                                            </button>
                                            {latestDistribution && (
                                                <button
                                                    className="flex items-center space-x-1 btn-outline btn-sm"
                                                    onClick={() => setDetailsContext({ bondId, bond })}
                                                >
                                                    <FileText className="w-3.5 h-3.5" />
                                                    <span>Details</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Bond Summary */}
                                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                                    {availableForAllocation === 0 && (
                                        <div className="flex items-start p-3 mb-4 border border-blue-200 rounded-md bg-blue-50">
                                            <CheckCircle2 className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                                            <div className="text-xs text-blue-800">
                                                <strong>Fully Allocated:</strong> All tokens for this bond have been distributed to investors. No remaining balance available for new allocations.
                                            </div>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-4">
                                        <div>
                                            <span className="text-gray-600">Total PS Allocation:</span>
                                            <div className="font-medium">{formatToken(psHeldTokens)}</div>
                                            <div className="text-xs text-gray-500">
                                                Total amount allocated to PS
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Already Distributed:</span>
                                            <div className="font-medium">{formatToken(totalDistributed)}</div>
                                            <div className="text-xs text-gray-500">To investors (approved)</div>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Remaining Balance:</span>
                                            <div className="font-medium">{formatToken(availableForAllocation)}</div>
                                            <div className="text-xs text-gray-500">
                                                {availableForAllocation === 0 ? 'Fully allocated' : 'Available for new allocations'}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Investors:</span>
                                            <div className="font-medium">{bondSubInvestors.length}</div>
                                            <div className="text-xs text-gray-500">With LOC submitted</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Investors Table */}
                                {displayInvestors.length > 0 ? (
                                    <>
                                        <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-gray-200">
                                            <div className="text-sm text-gray-600">
                                                {displayInvestors.length} investor{displayInvestors.length > 1 ? 's' : ''} found
                                            </div>
                                            <button
                                                className={`btn-primary btn-sm flex items-center gap-1`}
                                                onClick={() => {
                                                    // Only create allocations for investors who don't have existing requests or approvals
                                                    const allocations = displayInvestors
                                                        .filter(inv => {
                                                            const invId = inv.userId;
                                                            const editKey = `${bondId}-${invId}`;
                                                            // Skip already approved or those with pending requests
                                                            return !approvedAllocations[editKey] && !bondTokenRequests[editKey];
                                                        })
                                                        .map(inv => {
                                                            const invId = inv.userId;
                                                            const editKey = `${bondId}-${invId}`;
                                                            const defaultAllocation = inv.bondData?.totalAmount || 0;
                                                            const amount = manualAllocations[editKey] !== undefined
                                                                ? parseFloat(manualAllocations[editKey])
                                                                : defaultAllocation;

                                                            return {
                                                                investorId: invId,
                                                                tokenAmount: amount
                                                            };
                                                        }); if (allocations.length === 0) {
                                                            toast.info('All investors have already been allocated or have pending requests');
                                                            return;
                                                        }                                                    // Validate total doesn't exceed PS held tokens
                                                    const totalAmount = allocations.reduce((sum, a) => sum + a.tokenAmount, 0);
                                                    if (totalAmount > availableForAllocation) {
                                                        toast.error(`Total allocation (${formatToken(totalAmount)}) exceeds PS remaining balance (${formatToken(availableForAllocation)})`);
                                                        return;
                                                    }

                                                    // Check for invalid amounts
                                                    const hasInvalidAmounts = allocations.some(a => isNaN(a.tokenAmount) || a.tokenAmount <= 0);
                                                    if (hasInvalidAmounts) {
                                                        toast.error('All allocation amounts must be valid positive numbers');
                                                        return;
                                                    }

                                                    bulkAllocationMutation.mutate({ bondId, allocations });
                                                }}
                                            >
                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                <span>{bulkAllocationMutation.isLoading ? 'Approving All...' : 'Bulk Approve All'}</span>
                                            </button>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="table">
                                                <thead className="table-header">
                                                    <tr>
                                                        <th className="table-header-cell">Investor</th>
                                                        <th className="table-header-cell">Email</th>
                                                        <th className="table-header-cell">Token Allocation</th>
                                                        <th className="table-header-cell">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {displayInvestors.map((investor) => {
                                                        const investorId = investor.userId;
                                                        const editKey = `${bondId}-${investorId}`;
                                                        const isEditing = editingInvestor === editKey;
                                                        const defaultAllocation = investor.bondData?.totalAmount || 0;
                                                        const currentAllocation = manualAllocations[editKey] !== undefined
                                                            ? manualAllocations[editKey]
                                                            : defaultAllocation;
                                                        const approvalInfo = approvedAllocations[editKey];
                                                        const isApproved = !!approvalInfo;
                                                        const bondTokenRequest = bondTokenRequests[editKey];
                                                        const hasRequest = !!bondTokenRequest;
                                                        const isCustodian = investor.isCustodian;
                                                        const isDirect = investor.isDirect;

                                                        return (
                                                            <tr key={investorId} className="hover:bg-gray-50">
                                                                <td className="px-6 py-4">
                                                                    <div className="flex items-center gap-2">
                                                                        <div>
                                                                            <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                                                                                {isCustodian ? investor.custodianName || 'Unnamed Custodian' : investor.name || 'Unnamed'}
                                                                                {isCustodian && (
                                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                                        Custodian
                                                                                    </span>
                                                                                )}
                                                                                {isDirect && (
                                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                                        Direct
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <div className="font-mono text-xs text-gray-500">ID: {investor.userId}</div>
                                                                            {isCustodian && (
                                                                                <div className="mt-1 text-xs text-gray-500">
                                                                                    {investor.subInvestors?.length || 0} sub-investor{investor.subInvestors?.length !== 1 ? 's' : ''}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        {isApproved && (
                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                                                                Approved
                                                                            </span>
                                                                        )}
                                                                        {!isApproved && hasRequest && bondTokenRequest.status === 'completed' && (
                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                                                                Completed
                                                                            </span>
                                                                        )}
                                                                        {!isApproved && hasRequest && bondTokenRequest.status !== 'completed' && (
                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                                <Clock className="w-3 h-3 mr-1" />
                                                                                Request Sent
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4 text-sm text-gray-600">
                                                                    {isCustodian ? (investor.custodianEmail || '—') : (investor.email || '—')}
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    {isApproved ? (
                                                                        <div>
                                                                            <div className="text-sm font-medium text-green-700">
                                                                                {formatToken(approvalInfo.remainingTokenAmount)}
                                                                            </div>
                                                                            <div className="text-xs text-gray-500">
                                                                                Allocated on {new Date(approvalInfo.createdAt).toLocaleDateString()}
                                                                            </div>
                                                                        </div>
                                                                    ) : isEditing ? (
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            step="0.0001"
                                                                            value={currentAllocation}
                                                                            onChange={(e) => {
                                                                                setManualAllocations(prev => ({
                                                                                    ...prev,
                                                                                    [editKey]: e.target.value
                                                                                }));
                                                                            }}
                                                                            className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                                                            autoFocus
                                                                        />
                                                                    ) : (
                                                                        <div className="text-sm font-medium text-gray-900">
                                                                            {formatToken(currentAllocation)}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    {isApproved ? (
                                                                        <div className="text-xs text-gray-500">
                                                                            Allocation completed
                                                                            {approvalInfo.blockchainTxHash && (
                                                                                <div className="mt-1 font-mono">
                                                                                    TX: {approvalInfo.blockchainTxHash.slice(0, 10)}...
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ) : hasRequest ? (
                                                                        <div className="text-xs text-gray-500">
                                                                            <div className={`font-medium ${bondTokenRequest.status === 'completed' ? 'text-green-700' : 'text-blue-700'}`}>
                                                                                {bondTokenRequest.status === 'completed' ? 'Request Completed' : 'Request Pending'}
                                                                            </div>
                                                                            <div className="mt-1">
                                                                                Amount: {formatToken(bondTokenRequest.bondTokenAmount)}
                                                                            </div>
                                                                            <div className="mt-1">
                                                                                Status: <span className="capitalize">{bondTokenRequest.status?.replace('_', ' ')}</span>
                                                                            </div>
                                                                            <div className="mt-1 text-gray-400">
                                                                                {bondTokenRequest.status === 'completed' ? 'Allocation completed' : 'Awaiting investor approval'}
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex items-center gap-2">
                                                                            {isEditing ? (
                                                                                <>
                                                                                    <button
                                                                                        disabled={!currentAllocation || parseFloat(currentAllocation) <= 0}
                                                                                        onClick={() => {
                                                                                            const amount = parseFloat(currentAllocation);
                                                                                            if (isNaN(amount) || amount <= 0) {
                                                                                                toast.error('Please enter a valid bond amount');
                                                                                                return;
                                                                                            }

                                                                                            // Calculate total PENDING allocations for this bond (exclude approved)
                                                                                            const totalAllocated = displayInvestors.reduce((sum, cust) => {
                                                                                                const custId = cust.userId;
                                                                                                const custEditKey = `${bondId}-${custId}`;
                                                                                                // Skip if already approved
                                                                                                if (approvedAllocations[custEditKey]) {
                                                                                                    return sum;
                                                                                                }
                                                                                                // Use the new amount if it's the current custodian, otherwise use existing allocation
                                                                                                const custAmount = custEditKey === editKey
                                                                                                    ? amount
                                                                                                    : (manualAllocations[custEditKey] !== undefined
                                                                                                        ? parseFloat(manualAllocations[custEditKey])
                                                                                                        : (cust.bondData?.totalAmount || 0));
                                                                                                return sum + custAmount;
                                                                                            }, 0);

                                                                                            if (totalAllocated > psHeldTokens) {
                                                                                                toast.error(
                                                                                                    `Total allocation (${formatToken(totalAllocated)}) exceeds total PS allocation (${formatToken(psHeldTokens)})`
                                                                                                );
                                                                                                return;
                                                                                            }

                                                                                            // Store the parsed number value
                                                                                            setManualAllocations(prev => ({
                                                                                                ...prev,
                                                                                                [editKey]: amount
                                                                                            }));
                                                                                            setEditingInvestor(null);
                                                                                            toast.success('Allocation amount updated');
                                                                                        }}
                                                                                        className="flex items-center gap-1 btn-primary btn-sm"
                                                                                    >
                                                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                                                        Confirm
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setEditingInvestor(null);
                                                                                            setManualAllocations(prev => {
                                                                                                const updated = { ...prev };
                                                                                                delete updated[editKey];
                                                                                                return updated;
                                                                                            });
                                                                                        }}
                                                                                        className="btn-outline btn-sm"
                                                                                    >
                                                                                        Cancel
                                                                                    </button>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <button
                                                                                        onClick={() => setEditingInvestor(editKey)}
                                                                                        className={`btn-outline btn-sm flex items-center gap-1`}
                                                                                    >
                                                                                        <Eye className="w-3.5 h-3.5" />
                                                                                        Edit
                                                                                    </button>
                                                                                    <button
                                                                                        className={`btn-primary btn-sm flex items-center gap-1`}
                                                                                        onClick={() => {
                                                                                            const amount = parseFloat(currentAllocation);
                                                                                            if (isNaN(amount) || amount <= 0) {
                                                                                                toast.error('Please enter a valid bond amount');
                                                                                                return;
                                                                                            }

                                                                                            // Calculate total PENDING allocations for this bond (exclude approved)
                                                                                            const totalAllocated = displayInvestors.reduce((sum, inv) => {
                                                                                                const invId = inv.userId;
                                                                                                const invEditKey = `${bondId}-${invId}`;
                                                                                                // Skip if already approved
                                                                                                if (approvedAllocations[invEditKey]) {
                                                                                                    return sum;
                                                                                                }
                                                                                                // Use the new amount if it's the current investor, otherwise use existing allocation
                                                                                                const invAmount = invId === investorId
                                                                                                    ? amount
                                                                                                    : (manualAllocations[invEditKey] !== undefined
                                                                                                        ? parseFloat(manualAllocations[invEditKey])
                                                                                                        : (inv.bondData?.totalAmount || 0));
                                                                                                return sum + invAmount;
                                                                                            }, 0);

                                                                                            if (totalAllocated > psHeldTokens) {
                                                                                                toast.error(
                                                                                                    `Total allocation (${formatToken(totalAllocated)}) would exceed total PS allocation (${formatToken(psHeldTokens)})`
                                                                                                );
                                                                                                return;
                                                                                            }

                                                                                            allocationMutation.mutate({
                                                                                                bondId,
                                                                                                investorId: investorId,
                                                                                                amount
                                                                                            });
                                                                                        }}
                                                                                    >
                                                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                                                        {allocationMutation.isLoading ? 'Creating...' : 'Approve'}
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                ) : (
                                    <div className="px-6 py-12 text-center bg-gray-50">
                                        <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                        <h3 className="mb-1 text-sm font-medium text-gray-900">No Investors Yet</h3>
                                        <p className="text-xs text-gray-600">
                                            No investors have subscribed through this PS for this bond yet. Use "Upload CSV" to create allocations.
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Pagination Controls */}
                {pagination.totalPages > 1 && (
                    <div className="card mt-6">
                        <div className="card-body">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                {/* Items per page selector */}
                                <div className="flex items-center gap-2">
                                    <label htmlFor="itemsPerPage" className="text-sm text-gray-700">
                                        Items per page:
                                    </label>
                                    <select
                                        id="itemsPerPage"
                                        value={itemsPerPage}
                                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                        className="form-select text-sm border-gray-300 rounded-md"
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                    </select>
                                    <span className="text-sm text-gray-600">
                                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, pagination.total)} of {pagination.total}
                                    </span>
                                </div>

                                {/* Pagination buttons */}
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handlePageChange(1)}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        First
                                    </button>
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        Previous
                                    </button>

                                    {/* Page numbers */}
                                    <div className="flex items-center gap-1">
                                        {[...Array(pagination.totalPages)].map((_, index) => {
                                            const pageNumber = index + 1;
                                            // Show first page, last page, current page, and pages around current
                                            if (
                                                pageNumber === 1 ||
                                                pageNumber === pagination.totalPages ||
                                                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                                            ) {
                                                return (
                                                    <button
                                                        key={pageNumber}
                                                        onClick={() => handlePageChange(pageNumber)}
                                                        className={`px-3 py-1 text-sm border rounded-md ${currentPage === pageNumber
                                                                ? 'bg-primary-600 text-white border-primary-600'
                                                                : 'border-gray-300 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {pageNumber}
                                                    </button>
                                                );
                                            } else if (
                                                pageNumber === currentPage - 2 ||
                                                pageNumber === currentPage + 2
                                            ) {
                                                return <span key={pageNumber} className="px-2">...</span>;
                                            }
                                            return null;
                                        })}
                                    </div>

                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === pagination.totalPages}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        Next
                                    </button>
                                    <button
                                        onClick={() => handlePageChange(pagination.totalPages)}
                                        disabled={currentPage === pagination.totalPages}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        Last
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Upload Modal */}
            {uploadContext && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-medium text-gray-900">
                                    Upload Bond Token Distribution for {uploadContext.bond?.bondName || uploadContext.bondId}
                                </h3>
                                <button
                                    onClick={() => {
                                        setUploadContext(null);
                                        setUploadFile(null);
                                        setCsvErrors([]);
                                        setCsvWarnings([]);
                                        setCsvStats(null);
                                        setParsedData([]);
                                        setShowPreview(false);
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                            {/* Bond Summary */}
                            <div className="p-4 mb-6 rounded-lg bg-gray-50">
                                <h4 className="mb-3 text-sm font-medium text-gray-900">Bond Summary</h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-600">Bond Name:</span>
                                        <div className="font-medium">{uploadContext.bond?.bondName || '—'}</div>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">ISIN:</span>
                                        <div className="font-medium">{uploadContext.bond?.isin || '—'}</div>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">PS Held Tokens:</span>
                                        <div className="font-medium">{formatToken(uploadContext.psHeldTokens)}</div>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Product Type:</span>
                                        <div className="font-medium">{uploadContext.bond?.productType === "Fixed Notes" ? "Conventional" : (uploadContext.bond?.productType || '—')}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Upload Instructions */}
                            <div className="p-4 mb-6 border border-blue-200 rounded-md bg-blue-50">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <h4 className="mb-2 text-sm font-medium text-blue-900">CSV Upload Instructions</h4>
                                        <ul className="space-y-1 text-xs text-blue-800 list-disc list-inside">
                                            <li>Required columns: <strong>Investor ID</strong>, <strong>LEI</strong>, <strong>Bond Amount</strong></li>
                                            <li>Use actual Investor User IDs (24-character MongoDB ObjectId format)</li>
                                            <li><strong>Investor must be a Custodian or Direct Investor</strong> who submitted LOC through this PS for this bond</li>
                                            <li>LEI must be exactly 20 characters and match registered LEI in database</li>
                                            <li>Total bond amount cannot exceed PS held tokens</li>
                                            <li>File must be in CSV format, maximum size 5MB</li>
                                        </ul>
                                    </div>
                                    <button
                                        onClick={downloadTemplate}
                                        className="flex items-center ml-4 space-x-1 btn-outline btn-sm"
                                    >
                                        <Download className="w-3 h-3" />
                                        <span>Template</span>
                                    </button>
                                </div>
                            </div>

                            {/* File Upload Section */}
                            <div className="mb-6">
                                <label className="block mb-2 text-sm font-medium text-gray-700">
                                    Select CSV File
                                </label>
                                <input
                                    type="file"
                                    accept=".csv"
                                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                                    onChange={handleFileUpload}
                                />
                                {uploadFile && (
                                    <div className="flex items-center mt-2 text-xs text-gray-500">
                                        <FileText className="w-4 h-4 mr-1" />
                                        Selected: {uploadFile.name} ({Math.round(uploadFile.size / 1024)} KB)
                                    </div>
                                )}
                                {isUploading && (
                                    <div className="mt-3">
                                        <div className="flex items-center justify-between mb-1 text-xs text-gray-600">
                                            <span>Processing CSV...</span>
                                            <span>{uploadProgress}%</span>
                                        </div>
                                        <div className="w-full h-2 bg-gray-200 rounded-full">
                                            <div
                                                className="h-2 transition-all duration-300 bg-blue-600 rounded-full"
                                                style={{ width: `${uploadProgress}%` }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* CSV Errors */}
                            {csvErrors.length > 0 && (
                                <div className="p-4 mb-6 border border-red-200 rounded-md bg-red-50">
                                    <div className="flex items-start">
                                        <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                                        <div className="flex-1">
                                            <h4 className="mb-2 text-sm font-medium text-red-800">
                                                {csvErrors.length === 1 ? 'CSV Validation Issue' : `${csvErrors.length} CSV Validation Issues`}
                                            </h4>
                                            <div className="space-y-1 overflow-y-auto text-xs text-red-700 max-h-32">
                                                {csvErrors.map((error, index) => (
                                                    <div key={index}>• {error}</div>
                                                ))}
                                            </div>
                                            <div className="p-2 mt-2 text-xs text-orange-700 border border-orange-200 rounded bg-orange-50">
                                                Fix all issues above before you can upload the distribution.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* CSV Warnings */}
                            {csvWarnings.length > 0 && (
                                <div className="p-4 mb-6 border border-yellow-200 rounded-md bg-yellow-50">
                                    <div className="flex items-start">
                                        <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                                        <div className="flex-1">
                                            <h4 className="mb-2 text-sm font-medium text-yellow-800">
                                                {csvWarnings.length === 1 ? 'Validation Warning' : `${csvWarnings.length} Validation Warnings`}
                                            </h4>
                                            <div className="space-y-1 overflow-y-auto text-xs text-yellow-700 max-h-32">
                                                {csvWarnings.map((warning, index) => (
                                                    <div key={index} className="flex items-start">
                                                        <span className="mr-1">⚠️</span>
                                                        <span>{warning}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="p-2 mt-2 text-xs text-yellow-700 bg-yellow-100 border border-yellow-300 rounded">
                                                These are warnings that don't prevent upload, but you should review them carefully.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* CSV Stats */}
                            {csvStats && csvErrors.length === 0 && (
                                <div className="p-4 mb-6 border border-green-200 rounded-md bg-green-50">
                                    <div className="flex items-start">
                                        <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 mt-0.5" />
                                        <div className="flex-1">
                                            <h4 className="mb-2 text-sm font-medium text-green-800">CSV Processing Summary</h4>
                                            <div className="grid grid-cols-2 gap-4 text-xs text-green-700">
                                                <div>Total Rows: {csvStats.totalRows}</div>
                                                <div>Valid Rows: {csvStats.validRows}</div>
                                                <div>Total Bond Amount: {formatToken(csvStats.totalTokenAmount)}</div>
                                                <div>Remaining with PS: {formatToken(uploadContext.psHeldTokens - csvStats.totalTokenAmount)}</div>
                                            </div>
                                            {csvStats.serverValidated && (
                                                <div className="p-2 mt-2 text-xs text-blue-700 border border-blue-200 rounded bg-blue-50">
                                                    ✅ <strong>Database Validation:</strong> Investor IDs and LEIs have been validated against the database
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Preview Table */}
                            {showPreview && parsedData.length > 0 && (
                                <div className="mb-6">
                                    <h4 className="mb-3 text-sm font-medium text-gray-900">Distribution Preview</h4>
                                    <div className="overflow-x-auto border border-gray-200 rounded-md">
                                        <table className="table">
                                            <thead className="table-header">
                                                <tr>
                                                    <th className="table-header-cell">Investor ID</th>
                                                    <th className="table-header-cell">LEI</th>
                                                    <th className="table-header-cell">Bond Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {parsedData.map((row, index) => (
                                                    <tr key={index} className="hover:bg-gray-50">
                                                        <td className="px-3 py-2 font-mono text-sm text-gray-900">{row.investorId}</td>
                                                        <td className="px-3 py-2 font-mono text-sm text-gray-500">{row.lei}</td>
                                                        <td className="px-3 py-2 text-sm text-right text-gray-900">{formatToken(row.tokenAmount)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-between px-6 py-4 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setUploadContext(null);
                                    setUploadFile(null);
                                    setCsvErrors([]);
                                    setCsvWarnings([]);
                                    setCsvStats(null);
                                    setParsedData([]);
                                    setShowPreview(false);
                                }}
                                disabled={uploadMutation.isLoading}
                                className="btn-outline"
                            >
                                Cancel
                            </button>
                            <button
                                disabled={!uploadFile || uploadMutation.isLoading || csvErrors.length > 0 || !showPreview || parsedData.length === 0}
                                onClick={() => {
                                    if (!uploadFile) {
                                        toast.error('Please select a CSV file');
                                        return;
                                    }
                                    if (csvErrors.length > 0) {
                                        toast.error('Please fix CSV errors before uploading');
                                        return;
                                    }
                                    if (parsedData.length === 0) {
                                        toast.error('No valid data to upload');
                                        return;
                                    }
                                    // Create bond token requests from parsed CSV data
                                    uploadMutation.mutate({ bondId: uploadContext.bondId, allocations: parsedData });
                                }}
                                className="btn-primary"
                            >
                                {uploadMutation.isLoading ? (
                                    <>
                                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                                        Creating Requests...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4 mr-2" />
                                        Create Bond Token Requests
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {detailsContext && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Distribution Details</h3>
                                <p className="mt-1 text-xs text-gray-500">Bond: {detailsContext.bond?.bondName || detailsContext.bondId}</p>
                            </div>
                            <button
                                className="text-gray-400 hover:text-gray-600"
                                onClick={() => setDetailsContext(null)}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-6 py-4 overflow-y-auto">
                            {detailLoading ? (
                                <LoadingSpinner size="sm" text="Loading distribution..." />
                            ) : activeDistribution ? (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div className="card">
                                            <div className="space-y-2 card-body">
                                                <h4 className="text-sm font-semibold text-gray-700">Workflow</h4>
                                                <div className="flex items-center space-x-2">
                                                    {renderStatusBadge(activeDistribution)}
                                                    <span className="text-xs text-gray-500">{activeDistribution.rowCount} rows</span>
                                                </div>
                                                <div className="space-y-1 text-xs text-gray-600">
                                                    <div>Total tokens: {formatToken(activeDistribution.totalTokenAmount)}</div>
                                                    <div>Uploaded: {activeDistribution.uploadedAt ? formatDate(activeDistribution.uploadedAt) : '—'}</div>
                                                    <div>L1 Approved: {activeDistribution.l1ApprovedAt ? formatDate(activeDistribution.l1ApprovedAt) : '—'}</div>
                                                    <div>L2 Approved: {activeDistribution.l2ApprovedAt ? formatDate(activeDistribution.l2ApprovedAt) : '—'}</div>
                                                    <div>Executed: {activeDistribution.executedAt ? formatDate(activeDistribution.executedAt) : '—'}</div>
                                                </div>
                                                {activeDistribution.failureReason && (
                                                    <div className="flex items-center text-xs text-red-500">
                                                        <AlertCircle className="w-3.5 h-3.5 mr-1" />
                                                        {activeDistribution.failureReason}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="card">
                                            <div className="space-y-1 text-xs text-gray-600 card-body">
                                                <h4 className="text-sm font-semibold text-gray-700">Participants</h4>
                                                <div>Uploaded by: {activeDistribution.uploadedBy?.email || '—'}</div>
                                                <div>L1 approver: {activeDistribution.l1ApprovedBy?.email || '—'}</div>
                                                <div>L2 approver: {activeDistribution.l2ApprovedBy?.email || '—'}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card">
                                        <div className="card-header">
                                            <h4 className="text-sm font-semibold text-gray-700">Row Breakdown</h4>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="table">
                                                <thead className="table-header">
                                                    <tr>
                                                        <th className="table-header-cell">Investor</th>
                                                        <th className="table-header-cell">LEI</th>
                                                        <th className="table-header-cell">Tokens</th>
                                                        <th className="table-header-cell">Wallet</th>
                                                        <th className="table-header-cell">Status</th>
                                                        <th className="table-header-cell">Processed</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="table-body">
                                                    {activeDistribution.rows?.length ? activeDistribution.rows.map((row) => {
                                                        const badge = statusBadgeConfig[row.status] || statusBadgeConfig.staged;
                                                        const Icon = badge.icon;
                                                        return (
                                                            <tr key={row._id} className="table-row">
                                                                <td className="table-cell">
                                                                    <div className="text-sm text-secondary-900">{row.investorName || row.investorEmail || row.investorId}</div>
                                                                    <div className="text-xs text-gray-500">ID: {row.investorId}</div>
                                                                </td>
                                                                <td className="table-cell text-sm">{row.lei}</td>
                                                                <td className="table-cell text-sm">{formatToken(row.tokenAmount)}</td>
                                                                <td className="table-cell font-mono text-xs text-gray-600">{row.walletAddress}</td>
                                                                <td className="table-cell">
                                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                                                                        <Icon className="w-3.5 h-3.5 mr-1" />
                                                                        {badge.label}
                                                                    </span>
                                                                    {row.failureReason && (
                                                                        <div className="mt-1 text-xs text-red-500">{row.failureReason}</div>
                                                                    )}
                                                                </td>
                                                                <td className="table-cell text-xs text-gray-500">
                                                                    {row.processedAt ? formatDate(row.processedAt) : '—'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    }) : (
                                                        <tr>
                                                            <td colSpan={6} className="table-cell py-6 text-sm text-center text-gray-500">
                                                                No rows recorded in this distribution.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500">No distribution data available.</div>
                            )}
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t">
                            <button className="btn-primary" onClick={() => setDetailsContext(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BondTokenAllocation;
